import { useRef, useEffect } from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';
import { styled } from '@mui/system';
import { motion } from 'framer-motion';
import { Converter } from 'opencc-js/cn2t';

// Simplified -> Traditional (Taiwan) converter. ElevenLabs' STT may transcribe
// Mandarin speech as Simplified Chinese; for the Prudential TW demos (v7/v8) we
// force the displayed transcript to Traditional Chinese regardless of context.
const toTraditionalTW = Converter({ from: 'cn', to: 'tw' });

/**
 * Converts pronunciation-hack characters back to proper display characters.
 * The TTS uses 宝 (bǎo, 3rd tone) for correct pronunciation,
 * but we display 保 (the correct written form).
 * Also removes any XML-like language tags that ElevenLabs may add.
 * When `toTraditional` is set, additionally converts Simplified -> Traditional (Taiwan).
 */
const normalizeDisplayText = (text: string, toTraditional = false): string => {
  const out = text
    .replace(/宝诚/g, '保诚')    // Prudential
    .replace(/宝单/g, '保单')    // insurance policy
    .replace(/宝险/g, '保险')    // insurance
    .replace(/<Chinese>|<\/Chinese>|<English>|<\/English>/gi, '') // Remove language tags
    .replace(/\[[^\[\]]*\]/g, ''); // Remove bracketed text like [감사합니다]
  return toTraditional ? toTraditionalTW(out) : out;
};

const MessageBubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isAgent',
})<{ isAgent: boolean }>(({ isAgent }) => ({
  display: 'inline-block',
  maxWidth: '80%',
  padding: '12px 16px',
  borderRadius: '12px',
  backgroundColor: isAgent ? '#E8F4FD' : '#F0F7FF',
  color: '#333',
  ...(isAgent
    ? { borderTopLeftRadius: 0 }
    : { borderTopRightRadius: 0, marginLeft: 'auto' }),
}));

const TimeStamp = styled(Typography)({
  fontSize: '0.7rem',
  color: 'rgba(0, 0, 0, 0.54)',
  marginTop: 2,
});

const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isAgent',
})<{ isAgent: boolean }>(({ isAgent }) => ({
  marginBottom: 12,
  display: 'flex',
  flexDirection: 'column',
  alignItems: isAgent ? 'flex-start' : 'flex-end',
}));

const TypingIndicator = styled(motion.div)(() => ({
  display: 'flex',
  gap: 6,
  padding: '12px 16px',
  borderRadius: '12px',
  backgroundColor: '#E8F4FD',
  borderTopLeftRadius: 0,
}));

const TypingDot = styled(motion.div)(() => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: '#0064CC',
}));

interface PSConversationAreaProps {
  transcript: Array<{
    source: string;
    message: string;
    timestamp: Date;
  }>;
  isSpeaking: boolean;
  currentQuestion?: string;
  agentName?: string;
  version?: number;
}

export function PSConversationArea({
  transcript,
  isSpeaking,
  currentQuestion,
  agentName = 'Raihana',
  version,
}: PSConversationAreaProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript, isSpeaking]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ textAlign: 'center', py: 1, bgcolor: '#f8f9fa' }}>
        <Typography variant="body2" sx={{ color: '#0064CC', fontWeight: 600 }}>
          {version === 8 ? '保誠人壽 續期保費授權扣款確認' : version === 7 ? '保誠人壽 新契約承保前電話訪問' : version === 6 ? '日本損保 アウトバウンドコール' : version === 5 ? '기존고객 보험리뷰 | 고객 약속 잡기' : 'Prudential Post-Sales Survey'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {version === 8 ? '授權扣款確認' : version === 7 ? '承保前確認' : version === 6 ? '保障内容点検・相談日程調整' : version === 5 ? '고객 약속 잡기' : 'Quality Assurance Call'}
        </Typography>
      </Box>

      <Divider />

      <Box
        ref={scrollRef}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 3,
          bgcolor: '#fafbfc',
        }}
      >
        {transcript.map((item, index) => (
          <MessageContainer key={index} isAgent={item.source === 'ai'}>
            {item.source === 'ai' && (
              <Typography variant="caption" sx={{ color: '#666', mb: 0.5 }}>
                {agentName} ({version === 7 || version === 8 ? '保誠人壽' : version === 6 ? '日本損保' : version === 5 ? '휴먼생명보험' : 'Prudential'})
              </Typography>
            )}
            <MessageBubble isAgent={item.source === 'ai'} elevation={0}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {normalizeDisplayText(item.message, version === 7 || version === 8)}
              </Typography>
            </MessageBubble>
            <TimeStamp>
              {item.timestamp.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </TimeStamp>
          </MessageContainer>
        ))}

        {isSpeaking && (
          <MessageContainer isAgent={true}>
            <Typography variant="caption" sx={{ color: '#666', mb: 0.5 }}>
              {agentName} ({version === 7 || version === 8 ? '保誠人壽' : version === 6 ? '日本損保' : version === 5 ? '휴먼생명보험' : 'Prudential'})
            </Typography>
            <TypingIndicator>
              <TypingDot
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 1, repeat: Infinity }}
              />
              <TypingDot
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
              />
              <TypingDot
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
              />
            </TypingIndicator>
          </MessageContainer>
        )}
      </Box>

      {/* Current Question Indicator - hidden for v5/v6 (non-English UI) and v7/v8 (no SG survey topic) */}
      {currentQuestion && version !== 6 && version !== 5 && version !== 7 && version !== 8 && (
        <Box
          sx={{
            p: 2,
            bgcolor: '#f0f7ff',
            borderTop: '1px solid #e0e0e0',
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: '#666', display: 'block', mb: 0.5 }}
          >
            Current Survey Topic:
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#0064CC', fontWeight: 500 }}
          >
            {currentQuestion}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
